import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/shell/PageHeader";
import { useAuth } from "@/state/AuthProvider";
import { useAppState } from "@/state/AppStateProvider";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { KeyRound, LogOut, Download, ShieldCheck, RotateCw, Sparkles, Inbox, CheckCircle2, AlertTriangle, ServerCog, LockKeyhole, PlugZap, Globe, Loader2, Trash2 } from "lucide-react";
import { createInitialModelSettings, resolveDefaultModel, providerKind } from "@/lib/modelProviderSettings";
import { providersApi, blogsApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

const LS_KEY = "adams_ai_mock_state_v2";

export default function Settings() {
  const { user, logout } = useAuth();
  const {
    state,
    resetMockData,
    updateModelSettings,
    saveModelProviderCredential,
    testModelProvider,
    removeModelProvider,
    setModelProviderEnabled,
  } = useAppState();
  const modelSettings = state.modelSettings?.providers?.length ? state.modelSettings : createInitialModelSettings(state.modelSettings || {});
  const effectiveDefaultProviderId = resolveDefaultModel(modelSettings).providerId;

  const onExport = () => {
    try {
      const data = window.localStorage.getItem(LS_KEY) || "{}";
      const blob = new Blob([data], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `social-content-engine-export-${Date.now()}.json`;
      a.click();
      toast.success("Export downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const onTestProvider = async (providerId) => {
    const provider = modelSettings.providers.find((item) => item.id === providerId);
    const label = provider?.label || "Provider";
    const configured = provider?.configured || provider?.maskedKey || provider?.endpoint || provider?.local;
    if (!configured) {
      toast.error("Add a key or endpoint before testing");
      return;
    }
    const pending = toast.loading(`Testing ${label}…`);
    const result = await testModelProvider(providerId);
    if (result?.ok) {
      toast.success(`${label} connected${result.model ? ` — ${result.model}` : ""}`, { id: pending });
    } else if (result) {
      toast.error(`${label}: ${result.message || "connection failed"}`, { id: pending });
    } else {
      toast.message(`${label} connection checked`, { id: pending });
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="settings-page">
      <PageHeader
        eyebrow="Settings"
        title="Control your models, keys, and safeguards."
        description="Bring your own model keys here so Create can stay focused on the agent workflow."
        testId="settings-page-header"
        actions={
          <>
            <Button size="sm" variant="outline" onClick={() => { resetMockData(); toast.success("Local workspace reset"); }} className="gap-1.5" data-testid="settings-hero-reset"><RotateCw className="h-4 w-4" /> Reset local data</Button>
            <Button size="sm" onClick={onExport} className="gap-1.5" data-testid="settings-hero-export" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}><Download className="h-4 w-4" /> Export workspace</Button>
          </>
        }
      />

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        <div className="space-y-4 xl:col-span-8">
          <div className="rounded-xl border bg-card p-5 inner-highlight" style={{ borderColor: "var(--border-subtle)" }} data-testid="settings-provider-section">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-1">Model provider keys</h2>
                <p className="mt-1 text-[12.5px] text-muted-foreground">Add or update provider credentials. Raw keys are masked immediately and are not shown again.</p>
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full border bg-[color:var(--surface-2)] px-2.5 py-1 text-[11px] text-muted-foreground" style={{ borderColor: "var(--border-subtle)" }}>
                <LockKeyhole className="h-3 w-3" /> Encrypted backend storage
              </span>
            </div>
            {[
              { kind: "model", title: "Model providers", blurb: "LLMs that write, critique, and run utility tasks." },
              { kind: "research", title: "Research providers", blurb: "Power Web Research. More providers (Firecrawl, etc.) land in v2." },
            ].map(({ kind, title, blurb }) => {
              const group = [...modelSettings.providers]
                .filter((p) => providerKind(p) === kind)
                .sort((a, b) =>
                  (b.id === effectiveDefaultProviderId) - (a.id === effectiveDefaultProviderId)
                  || (b.configured ? 1 : 0) - (a.configured ? 1 : 0));
              if (group.length === 0) return null;
              return (
                <div key={kind} className="mt-5" data-testid={`settings-provider-group-${kind}`}>
                  <div className="mb-2 flex items-baseline gap-2">
                    <h3 className="text-[12.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{title}</h3>
                    <span className="text-[11.5px] text-muted-foreground">— {blurb}</span>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    {group.map((provider) => (
                      <ProviderCard
                        key={provider.id}
                        provider={provider}
                        activeDefault={effectiveDefaultProviderId === provider.id}
                        onSave={async (patch) => {
                          await saveModelProviderCredential(provider.id, patch);
                          toast.success(`${provider.label} saved`);
                        }}
                        onTest={() => onTestProvider(provider.id)}
                        onSetDefault={() => {
                          updateModelSettings({ defaultProvider: provider.id });
                          toast.success(`${provider.label} set as default`);
                        }}
                        onRemove={async () => {
                          const r = await removeModelProvider(provider.id);
                          r.ok ? toast.success(`${provider.label} key removed`) : toast.error(r.error || "Remove failed");
                        }}
                        onToggleEnabled={async (enabled) => {
                          const r = await setModelProviderEnabled(provider.id, enabled);
                          r.ok ? toast.message(`${provider.label} ${enabled ? "enabled" : "disabled"}`) : toast.error(r.error || "Update failed");
                        }}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="rounded-xl border bg-card p-5 inner-highlight" style={{ borderColor: "var(--border-subtle)" }}>
            <h2 className="text-sm font-semibold text-1">Approval & publishing</h2>
            <div className="mt-3 space-y-3">
              <Row label="Require approval before scheduling" desc="All generated assets must be approved before they appear on the calendar." checked={modelSettings.requireApproval} onChange={(value) => updateModelSettings({ requireApproval: value })} testid="settings-require-approval" />
              <Row label="Publishing safeguard" desc="Pause publish actions and ask for confirmation before anything goes live." checked={modelSettings.publishingGuard} onChange={(value) => updateModelSettings({ publishingGuard: value })} testid="settings-publishing-guard" />
              <Row label="Autosave drafts" desc="Saves work-in-progress to browser storage as you type." checked={modelSettings.autosave} onChange={(value) => updateModelSettings({ autosave: value })} testid="settings-autosave" />
            </div>
          </div>

          <BlogTargetCard />
        </div>

        <aside className="space-y-4 xl:col-span-4">
          <div className="rounded-xl border bg-card p-5 inner-highlight" style={{ borderColor: "var(--border-subtle)" }}>
            <h3 className="text-sm font-semibold text-1 flex items-center gap-2"><KeyRound className="h-4 w-4 text-[color:var(--accent-cyan)]" /> API key handling</h3>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Your keys are encrypted at rest by the local backend. After saving, only a masked fingerprint is shown — the raw key is never returned to the browser.</p>
          </div>

          <div className="rounded-xl border bg-card p-5 inner-highlight" style={{ borderColor: "var(--border-subtle)" }}>
            <h3 className="text-sm font-semibold text-1 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[color:var(--accent-green)]" /> Workspace safeguards</h3>
            <ul className="mt-3 space-y-2 text-xs text-foreground/85">
              <li className="flex items-start gap-2"><Sparkles className="mt-0.5 h-3 w-3 text-[color:var(--accent-cyan)]" /> Approval-first publishing keeps Create and Calendar gated.</li>
              <li className="flex items-start gap-2"><Inbox className="mt-0.5 h-3 w-3 text-[color:var(--accent-cyan)]" /> Ingestion endpoints can keep their own keys separate from model providers.</li>
              <li className="flex items-start gap-2"><ServerCog className="mt-0.5 h-3 w-3 text-[color:var(--accent-cyan)]" /> Local model endpoints can be configured without an API key.</li>
            </ul>
          </div>

          <div className="rounded-xl border bg-card p-5 inner-highlight" style={{ borderColor: "var(--border-subtle)" }}>
            <h3 className="text-sm font-semibold text-1">Account</h3>
            <div className="mt-3 space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Name</Label>
                <Input defaultValue={user?.name || "Adam"} data-testid="settings-name" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Email</Label>
                <Input defaultValue={user?.email} data-testid="settings-email" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => { logout(); toast.message("Signed out"); }} className="gap-1.5" data-testid="settings-signout"><LogOut className="h-4 w-4" /> Sign out</Button>
                <Button size="sm" variant="outline" onClick={() => { resetMockData(); toast.success("Local workspace reset"); }} className="gap-1.5" data-testid="settings-reset"><RotateCw className="h-4 w-4" /> Reset local data</Button>
              </div>
            </div>
          </div>
        </aside>
      </section>
    </motion.div>
  );
}

function BlogTargetCard() {
  const [target, setTarget] = useState(null);
  const [endpoint, setEndpoint] = useState("");
  const [secret, setSecret] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    blogsApi.getTarget().then((t) => {
      setTarget(t);
      setEndpoint(t?.endpoint_url || "");
      setEnabled(Boolean(t?.enabled));
    }).catch(() => {});
  }, []);

  const onSave = async () => {
    setBusy(true);
    try {
      const body = { endpoint_url: endpoint.trim(), enabled };
      if (secret.trim()) body.secret = secret.trim(); // only send when changed
      const t = await blogsApi.saveTarget(body);
      setTarget(t);
      setSecret("");
      toast.success("Blog publish target saved");
    } catch (err) {
      toast.error(`Could not save: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border bg-card p-5 inner-highlight" style={{ borderColor: "var(--border-subtle)" }} data-testid="settings-blog-target">
      <h2 className="text-sm font-semibold text-1 flex items-center gap-2"><Globe className="h-4 w-4 text-[color:var(--accent-cyan)]" /> Blog publishing</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Publish posts to your own site via a signed webhook. The app POSTs a standardized payload (markdown + html + images) to your endpoint; your receiver verifies the signature and saves it. See <span className="font-mono">docs/blog-publishing.md</span> for the contract + sample receiver.
      </p>
      <div className="mt-3 space-y-3">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Endpoint URL</Label>
          <Input value={endpoint} onChange={(e) => setEndpoint(e.target.value)} placeholder="https://yoursite.com/api/blog/ingest" data-testid="blog-target-endpoint" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Signing secret {target?.secret_set && <span className="ml-1 text-[10px] text-[color:var(--accent-green)]">• set</span>}</Label>
          <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={target?.secret_set ? "•••••••• (leave blank to keep)" : "a long random string"} data-testid="blog-target-secret" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} />
          <p className="text-[11px] text-muted-foreground">Used to HMAC-sign each request so your receiver can verify it's really us. Stored encrypted; never shown again.</p>
        </div>
        <Row label="Enable publishing" desc="Allow the Publish button to send posts to this endpoint." checked={enabled} onChange={setEnabled} testid="blog-target-enabled" />
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">{target?.last_published_at ? `Last publish: HTTP ${target.last_status} · ${new Date(target.last_published_at).toLocaleString()}` : "Not published yet"}</span>
          <Button size="sm" onClick={onSave} disabled={busy} className="gap-1.5" data-testid="blog-target-save">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function ProviderCard({ provider, activeDefault, onSave, onTest, onSetDefault, onRemove, onToggleEnabled }) {
  const [apiKey, setApiKey] = useState("");
  const [endpoint, setEndpoint] = useState(provider.endpoint || "");
  const [defaultModel, setDefaultModel] = useState(provider.defaultModel || "");
  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelsError, setModelsError] = useState("");

  const status = getProviderStatus(provider);

  const loadModels = useCallback(async () => {
    setLoadingModels(true);
    setModelsError("");
    try {
      const res = await providersApi.models(provider.id);
      setModels(res?.models || []);
      if (res?.error) setModelsError(res.error);
    } catch (_) {
      setModelsError("Could not load models");
    } finally {
      setLoadingModels(false);
    }
  }, [provider.id]);

  // Once a key is established, pull the live model list for the dropdown.
  useEffect(() => {
    if (provider.configured && !provider.local) loadModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider.configured]);

  return (
    <article
      className={cn("rounded-xl border p-4", activeDefault && "ring-1 ring-[color:var(--accent-cyan)]/35")}
      style={{ borderColor: activeDefault ? "rgba(143, 224, 74,0.34)" : "var(--border-subtle)", backgroundColor: "var(--surface-1)" }}
      data-testid={`settings-provider-${provider.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-1">{provider.label}</h3>
            {activeDefault && <span className="rounded-full bg-[color:var(--accent-blue)]/10 px-2 py-0.5 text-[10.5px] font-medium text-[color:var(--accent-cyan)]">Default</span>}
            <ProviderStatusPill status={status} />
          </div>
          <p className="mt-1 text-[12.5px] text-muted-foreground">{provider.description}</p>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={onTest} className="gap-1.5" data-testid={`settings-test-${provider.id}`}>
          <PlugZap className="h-3.5 w-3.5" /> Test
        </Button>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{provider.secretLabel}</Label>
          <Input
            type="password"
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
            placeholder={provider.maskedKey || provider.placeholder}
            autoComplete="off"
            data-testid={`settings-key-${provider.id}`}
            style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}
          />
        </div>
        {provider.supportsEndpoint && (
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Endpoint</Label>
            <Input
              value={endpoint}
              onChange={(event) => setEndpoint(event.target.value)}
              placeholder={provider.endpointPlaceholder || "https://..."}
              data-testid={`settings-endpoint-${provider.id}`}
              style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}
            />
          </div>
        )}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Default model</Label>
            {!provider.local && (
              <button
                type="button"
                onClick={loadModels}
                className="text-[10px] font-medium text-[color:var(--accent-cyan)] hover:underline disabled:opacity-50"
                disabled={loadingModels}
                data-testid={`settings-load-models-${provider.id}`}
              >
                {loadingModels ? "Loading…" : models.length ? "Refresh" : "Load models"}
              </button>
            )}
          </div>
          {models.length > 0 ? (
            <Select value={defaultModel || undefined} onValueChange={setDefaultModel}>
              <SelectTrigger data-testid={`settings-model-${provider.id}`} style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}>
                <SelectValue placeholder="Select a model" />
              </SelectTrigger>
              <SelectContent>
                {models.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={defaultModel}
              onChange={(event) => setDefaultModel(event.target.value)}
              placeholder={provider.defaultModel}
              data-testid={`settings-model-${provider.id}`}
              style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}
            />
          )}
          {modelsError ? <p className="text-[10px] text-amber-500">{modelsError}</p> : null}
        </div>
      </div>

      {/* Disable toggle: keep the key, stop using the provider. Only when there's a saved key. */}
      {provider.maskedKey && !provider.local && onToggleEnabled && (
        <div className="mt-3 flex items-center justify-between rounded-lg border px-3 py-2" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}>
          <div>
            <p className="text-[12.5px] font-medium text-1">Enabled</p>
            <p className="text-[11px] text-muted-foreground">{provider.enabled === false ? "Disabled — key kept, but this provider won't be used." : "On — available for generation/research."}</p>
          </div>
          <Switch checked={provider.enabled !== false} onCheckedChange={(v) => onToggleEnabled(v)} data-testid={`settings-enabled-${provider.id}`} />
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="text-[11.5px] text-muted-foreground">
          {provider.maskedKey ? <span className="font-mono">Saved key: {provider.maskedKey}</span> : provider.local ? "Local endpoint can run without a saved key." : "No key saved yet."}
          {provider.lastError ? <span className="ml-2 text-red-400">{provider.lastError}</span> : null}
        </div>
        <div className="flex items-center gap-2">
          {provider.maskedKey && !provider.local && onRemove && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="gap-1.5 text-muted-foreground hover:text-[color:var(--accent-red)]"
              onClick={() => { if (window.confirm(`Remove the saved ${provider.label} key? You'll need to paste it again to use this provider.`)) onRemove(); }}
              data-testid={`settings-remove-${provider.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" /> Remove key
            </Button>
          )}
          {provider.configured && !activeDefault && provider.enabled !== false && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onSetDefault}
              data-testid={`settings-set-default-${provider.id}`}
            >
              Set as default
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            onClick={async () => {
              await onSave({ apiKey, endpoint, defaultModel });
              setApiKey("");
              // Key is now established — populate the model dropdown.
              if (!provider.local) loadModels();
            }}
            data-testid={`settings-save-${provider.id}`}
            style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}
          >
            Save provider
          </Button>
        </div>
      </div>
    </article>
  );
}

function getProviderStatus(provider) {
  if (provider.configured && provider.enabled === false) return { label: "Disabled", tone: "slate", icon: KeyRound };
  if (provider.status === "connected") return { label: "Connected", tone: "green", icon: CheckCircle2 };
  if (provider.status === "error") return { label: "Needs attention", tone: "red", icon: AlertTriangle };
  if (provider.configured) return { label: "Saved", tone: "blue", icon: KeyRound };
  return { label: "Not configured", tone: "slate", icon: KeyRound };
}

function ProviderStatusPill({ status }) {
  const Icon = status.icon;
  const toneClass = {
    green: "bg-emerald-500/10 text-emerald-600",
    red: "bg-red-500/10 text-red-500",
    blue: "bg-[color:var(--accent-blue)]/10 text-[color:var(--accent-cyan)]",
    slate: "bg-white/[0.045] text-muted-foreground",
  }[status.tone] || "bg-white/[0.045] text-muted-foreground";

  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-medium", toneClass)}>
      <Icon className="h-3 w-3" /> {status.label}
    </span>
  );
}

function Row({ label, desc, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}>
      <div className="min-w-0">
        <p className="text-sm text-1">{label}</p>
        <p className="text-[11px] text-muted-foreground">{desc}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

